import { useEffect, useMemo, useState } from "react";
import { Drawer } from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { logout } from "../../features/auth/authActions";
import { resetStep, setFormData } from "../../features/formData/formSlice";
import {
  getAuthUser,
  getUserDisplayName,
} from "../../admin/navigation/adminNavigation";
import customerNavigation from "./customerNavigation";
import "./CustomerTabMenu.css";

const CustomerTabMenu = () => {
  const authState = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const authUser = getAuthUser(authState);
  const displayName = getUserDisplayName(authState).replace("Admin User", "Customer");
  const emailAddress = authUser?.email_address || authUser?.email || "";

  const activeItem = useMemo(
    () =>
      customerNavigation.navItems.find((item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(`${item.path}/`)
      ) ?? customerNavigation.navItems[0],
    [location.pathname]
  );

  const isItemActive = (itemPath) =>
    location.pathname === itemPath ||
    location.pathname.startsWith(`${itemPath}/`);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleNavItemClick = (event) => {
    const nextPath = event.currentTarget.dataset.path;

    if (!nextPath) {
      return;
    }

    navigate(nextPath);
  };

  const handleFooterItemClick = (event) => {
    const nextPath = event.currentTarget.dataset.path;

    if (!nextPath) {
      return;
    }

    navigate(nextPath);
  };

  const handleLogout = () => {
    dispatch(resetStep());
    dispatch(setFormData({}));
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    handleDrawerClose();
  }, [location.pathname]);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const itemActive = isItemActive(item.path);

    return (
      <button
        key={item.path}
        type="button"
        className={`ctm__item${itemActive ? " ctm__item--active" : ""}`}
        data-path={item.path}
        onClick={handleNavItemClick}
      >
        <span className="ctm__item-main">
          <span className="ctm__icon-wrap">
            <Icon size={18} strokeWidth={1.8} />
          </span>
          <span className="ctm__item-copy">
            <span className="ctm__label">{item.label}</span>
            <span className="ctm__description">{item.description}</span>
          </span>
        </span>
      </button>
    );
  };

  const renderFooterItem = (item) => {
    const Icon = item.icon;
    const itemActive = isItemActive(item.path);

    return (
      <button
        key={item.path}
        type="button"
        className={`ctm__footer-link${itemActive ? " ctm__footer-link--active" : ""}`}
        data-path={item.path}
        onClick={handleFooterItemClick}
      >
        <Icon size={16} strokeWidth={1.9} />
        <span>{item.label}</span>
      </button>
    );
  };

  const renderNavigation = () => (
    <div className="ctm__panel">
      <div className="ctm__section">
        <nav className="ctm__nav" aria-label="Customer dashboard navigation">
          {customerNavigation.navItems.map(renderNavItem)}
        </nav>
      </div>

      <div className="ctm__footer">
        <div className="ctm__footer-links">
          {customerNavigation.footerItems.map(renderFooterItem)}
        </div>

        <button
          type="button"
          className="ctm__ghost-action"
          onClick={handleLogout}
        >
          <LogOut size={16} strokeWidth={1.9} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="ctm">
      <button
        type="button"
        className="ctm__mobile-toggle"
        onClick={handleDrawerOpen}
        aria-label="Open customer navigation"
      >
        <Menu size={20} strokeWidth={2} />
        <span>Dashboard Menu</span>
      </button>

      <div className="ctm__desktop">{renderNavigation()}</div>

      <Drawer
        opened={isDrawerOpen}
        onClose={handleDrawerClose}
        position="left"
        size="xs"
        title="Customer Menu"
        classNames={{
          content: "ctm__drawer-content",
          header: "ctm__drawer-header",
          title: "ctm__drawer-title",
          body: "ctm__drawer-body",
        }}
      >
        {renderNavigation()}
      </Drawer>
    </div>
  );
};

export default CustomerTabMenu;
